import * as R from 'ramda';
import memoize from 'memoize-one';
import {
  getFrequencyArtefact as SDMXJS_getFrequencyArtefact,
  getRequestArgs,
  isFrequencyDimension,
  constrainDimensions,
  getSDMXUrl,
} from '@sis-cc/dotstatsuite-sdmxjs';
import Set from 'es6-set';
import {
  sdmxFormat,
  getFrequencies,
  parseDateFromSdmxPeriod,
  getFrequencyFromSdmxPeriod,
  getAjustedDate,
  getSdmxPeriod,
  getSubstructedDate,
} from '../lib/sdmx/frequency';
import { getDateInTheRange } from '../utils/date';
import {
  dataRequestSize,
  getDatasource as getSettingsDatasource,
  locales,
  sdmxPeriodBoundaries,
  getSpaceRangeSizeLimit as getSettingsRangeSizeLimit,
  isPaginationActivated,
} from '../lib/settings';
import {
  getLastNObservations,
  getLocale,
  getPeriod as getRouterPeriod,
  getHasDataAvailability,
  getDataquery,
  getDataflow,
  getDisplay,
  getLastNMode,
} from './router';
import {
  dataFileRequestArgsWrapper,
  getOnlyHasDataDimensions,
  rawDataRequestArgsWrapper,
} from '../lib/sdmx';
import { applyAutomatedSelection } from '../lib/sdmx/selection';
import { cleanDeadBranches } from '../utils/clean-dead-branches';
import { getMultiHierarchicalFilters } from '../lib/sdmx/hierarchical-codelist';
import { LASTNOBSERVATIONS, LASTNPERIODS } from '../utils/used-filter';
import { getTimePeriodBoundaries } from '../lib/sdmx/accessors/constraints';
import { cleanifyLayoutIds } from '../utils';
import {
  getTimePeriodArtefact as _getTPArtefact,
  getTimePeriodContraints,
} from '../lib/sdmx/structure';

const getSdmx = R.prop('sdmx');

const getMicrodataDimensionId = memoize((state) =>
  R.path(['microdata', 'dimensionId'], state),
);

export const getAnnotations = memoize((state) =>
  R.pathOr([], ['data', 'structure', 'annotations'], getSdmx(state)),
);

const _getDimensions = memoize((state) =>
  R.propOr([], 'dimensions', getSdmx(state)),
);
const getAttributes = memoize((state) =>
  R.propOr([], 'attributes', getSdmx(state)),
);
export const getPage = memoize((state) => R.propOr(0, 'page', getSdmx(state)));
export const getHiddenValuesAnnotation = memoize((state) =>
  R.prop('hiddenValuesAnnotation', getSdmx(state)),
);
export const getData = memoize((state) => R.prop('data', getSdmx(state)));
export const getExternalReference = memoize((state) =>
  R.prop('externalReference', getSdmx(state)),
);
const getConstraints = memoize((state) =>
  R.prop('constraints', getSdmx(state)),
);

export const getDataflowName = memoize((state) => {
  const sdmx = getSdmx(state);
  return R.isNil(sdmx.data)
    ? sdmx.name
    : R.path(['structure', 'name'], sdmx.data);
});

export const getHierarchies = memoize((state) =>
  R.prop('hierarchies', getSdmx(state)),
);
export const getHCodes = memoize((state) => R.prop('hCodes', getSdmx(state)));
export const getCombinationsDefinition = memoize((state) =>
  R.propOr([], 'combinationsDefinition', getSdmx(state)),
);
export const getParsedAttributes = memoize((state) =>
  R.propOr([], 'parsedAttributes', getSdmx(state)),
);
export const getIsAvailabilityDisabled = memoize((state) =>
  R.propOr(false, 'isAvailabilityDisabled', getSdmx(state)),
);
const _getDefaultLayoutIds = memoize((state) =>
  R.propOr({}, 'defaultLayoutIds', getSdmx(state)),
);
export const getActualContentConstraints = memoize((state) =>
  R.propOr([], 'actualContentConstraints', getSdmx(state)),
);
export const getActuallContentConstraintId = memoize((state) =>
  R.propOr(null, 'constraintId', getSdmx(state)),
);
const getTimePeriod = memoize((state) =>
  R.propOr(null, 'timePeriod', getSdmx(state)),
);
const getCustomDataRequestSize = memoize((state) =>
  R.prop('customDataRequestSize', getSdmx(state)),
);

export const getActualContentConstraint = memoize((state) => {
  const constraints = getActualContentConstraints(state);
  const constraintId = getActuallContentConstraintId(state);
  return R.find(R.propEq(constraintId, 'id'), constraints) ?? {};
});

export const getTimePeriodArtefact = memoize((state) => {
  const constraint = getActualContentConstraint(state);
  const timePeriod = getTimePeriod(state);
  const isTimePeriodDisable =
    R.isNil(timePeriod) || !R.prop('display', timePeriod);

  const timePeriodConstraint = getTimePeriodContraints(
    isTimePeriodDisable,
    timePeriod,
    R.propOr({}, 'cubeRegions', constraint),
  );

  return _getTPArtefact({
    timePeriod,
    isTimePeriodDisable,
    timePeriodBoundaries: R.propOr([], 'boundaries', timePeriodConstraint),
  });
});

export const getDimensions = memoize((state) => {
  const dimensions = _getDimensions(state);
  const constraint = getActualContentConstraint(state);
  return constrainDimensions(
    dimensions,
    R.propOr({}, 'cubeRegions', constraint),
  );
});

export const getAvailableTimePeriod = memoize((state) =>
  R.prop('availableTimePeriod', getConstraints(state)),
);

export const getFrequencyArtefact = memoize((state) => {
  const dimensions = getDimensions(state);
  const attributes = getAttributes(state);
  return SDMXJS_getFrequencyArtefact({ dimensions, attributes });
});

export const getDatasource = memoize((state) => {
  const dataflow = getDataflow(state);
  const externalReference = getExternalReference(state);
  if (!R.isNil(externalReference)) return externalReference.datasource;
  return getSettingsDatasource(R.prop('datasourceId', dataflow)) ?? {};
});

export const getRawStructureRequestArgs = memoize((state) => {
  const dataflow = getDataflow(state);
  const locale = getLocale(state);
  const externalReference = getExternalReference(state);

  if (!R.isNil(externalReference))
    return R.assoc('locale', locale, externalReference);

  return {
    datasource: getSettingsDatasource(R.prop('datasourceId', dataflow)),
    identifiers: {
      code: R.prop('dataflowId', dataflow),
      ...R.pick(['agencyId', 'version'], dataflow),
    },
    locale,
  };
});

export const getDefaultLayoutIds = memoize((state) =>
  cleanifyLayoutIds(_getDefaultLayoutIds(state)),
);

export const getDataRequestRange = memoize((state) => {
  const page = isPaginationActivated ? getPage(state) : 0;
  const pageSize = isPaginationActivated
    ? dataRequestSize
    : getCustomDataRequestSize(state) || dataRequestSize;
  return [page * pageSize, (page + 1) * pageSize - 1];
});

export const getSpaceRangeSizeLimit = memoize((state) => {
  const dataflow = getDataflow(state);
  return getSettingsRangeSizeLimit(dataflow.datasourceId);
});

export const getStructureRequestArgs = memoize((state) => {
  const args = getRawStructureRequestArgs(state);
  return getRequestArgs({
    ...args,
    type: 'dataflow',
    withPartialReferences: true,
  });
});

const getHierarchisedDimensions = memoize((state) => {
  const dimensions = getDimensions(state);
  const hierarchies = getHierarchies(state);
  return getMultiHierarchicalFilters(dimensions, hierarchies);
});

export const getDimensionsWithDataQuerySelection = memoize(
  (state, { automatedSelections } = {}) => {
    const filters = getHierarchisedDimensions(state);
    const dataquery = getDataquery(state);
    const microdataDimId = getMicrodataDimensionId(state);
    const dqParts = R.isNil(dataquery) ? [] : dataquery.split('.');

    return R.pipe(
      R.addIndex(R.map)((_filter, index) => {
        if (R.isEmpty(_filter)) return _filter;

        let filter = _filter;
        if (R.has(_filter.id, automatedSelections)) {
          filter = R.over(R.lensProp('values'), (values) =>
            applyAutomatedSelection(
              values,
              R.prop(_filter.id, automatedSelections),
            ),
          )(_filter);
        }

        const valIdsStr = R.nth(index, dqParts);
        if (R.isNil(valIdsStr) || R.isEmpty(valIdsStr)) return filter;

        const valueIdsSet = new Set(R.split('+', valIdsStr));
        return R.over(
          R.lensProp('values'),
          R.map((val) =>
            valueIdsSet.has(val.id) ? { ...val, isSelected: true } : val,
          ),
          filter,
        );
      }),
      R.reject(
        R.anyPass([
          R.pipe(R.prop('display'), R.not),
          R.propEq(microdataDimId, 'id'),
          isFrequencyDimension,
          R.pipe(R.prop('values'), R.length, R.gte(1)),
        ]),
      ),
    )(filters);
  },
);

export const getFilters = memoize(
  (state, { availableConstraints = {}, automatedSelections = {} } = {}) => {
    const hasDataAvailability = getHasDataAvailability(state);
    const filters = getDimensionsWithDataQuerySelection(state, {
      automatedSelections,
    });
    const hiddenIds = getHiddenValuesAnnotation(state) ?? {};

    return R.pipe(
      (dimensions) =>
        constrainDimensions(dimensions, availableConstraints, 'isEnabled'),
      R.when(
        R.always(hasDataAvailability),
        R.map(R.over(R.lensProp('values'), cleanDeadBranches)),
      ),
      R.filter((filter) => {
        const values = R.propOr([], 'values', filter);
        const hasEnoughValues = values.length > 1;
        const isNotAllHidden = hiddenIds[filter.id]
          ? !R.isEmpty(
              R.difference(R.pluck('id', values), hiddenIds[filter.id]),
            )
          : true;
        return hasEnoughValues && isNotAllHidden;
      }),
    )(filters);
  },
);

export const getAvailableFrequencies = memoize((state) => {
  const frequencyArtefact = getFrequencyArtefact(state);
  if (R.isNil(frequencyArtefact)) return {};

  return R.pipe(
    R.ifElse(
      R.always(getHasDataAvailability(state)),
      R.pipe(R.of(Array), getOnlyHasDataDimensions, R.head),
      R.identity,
    ),
    getFrequencies,
  )(frequencyArtefact);
});

export const getFrequency = memoize((state) => {
  const routerPeriod = getRouterPeriod(state);
  const frequencyArtefact = getFrequencyArtefact(state);
  if (!frequencyArtefact) return;

  const dataquery = getDataquery(state) ?? '';
  const availableFrequencies = getAvailableFrequencies(state) ?? {};

  const splitDataquery = R.split('.', dataquery);
  const frequency = R.nth(frequencyArtefact.index, splitDataquery);

  if (!frequencyArtefact.isAttribute && !R.isNil(frequency)) return frequency;

  const frequencyFromPeriod = getFrequencyFromSdmxPeriod(routerPeriod);
  if (!R.isNil(frequencyFromPeriod)) return frequencyFromPeriod;

  if (frequencyArtefact.isAttribute)
    return R.head(R.keys(availableFrequencies));
  return;
});

export const getDatesBoundaries = memoize((state) => {
  const artefact = getTimePeriodArtefact(state);
  const frequency = getFrequency(state);
  if (R.isNil(artefact)) return null;

  const boundaries = getHasDataAvailability(state)
    ? R.propOr([], 'timePeriodBoundaries', artefact)
    : sdmxPeriodBoundaries;

  return R.map(getAjustedDate(frequency), boundaries);
});

export const getPeriod = memoize((state) => {
  const sdmxPeriod = getRouterPeriod(state);
  const frequency = getFrequency(state);
  const boundaries = getDatesBoundaries(state);

  if (R.isNil(sdmxPeriod)) return [undefined, undefined];
  const period = parseDateFromSdmxPeriod(frequency, sdmxPeriod);

  return R.ifElse(
    R.pipe(R.length, R.equals(2)),
    R.map(getDateInTheRange(boundaries)),
    R.always(R.map(getDateInTheRange(boundaries), sdmxPeriod)),
  )(period);
});

export const getTimeFormats = memoize((state) => {
  const locale = getLocale(state);
  return R.mergeRight(sdmxFormat, {
    M: R.pathOr('yyyy MMM', [locale, 'timeFormat'], locales),
  });
});

// --- API Request Args & URL Generators ---

export const getDataRequestParams = memoize((state) => {
  const _period = getRouterPeriod(state);
  const lastNValue = getLastNObservations(state);
  const availableTimePeriod = getAvailableTimePeriod(state);
  const lastNMode = getLastNMode(state);
  const frequency = getFrequency(state);
  const boundaries = getTimePeriodBoundaries(availableTimePeriod);
  const lastNObservations = R.equals(lastNMode, LASTNOBSERVATIONS)
    ? lastNValue
    : null;

  if (!R.isNil(_period)) {
    return {
      lastNObservations,
      startPeriod: R.head(_period),
      endPeriod: R.last(_period),
    };
  }

  const period = [undefined, undefined];
  const formattedAvailablePeriod = R.ifElse(
    R.either(R.isNil, R.isEmpty),
    R.always([undefined, undefined]),
    R.map((p) => (R.isNil(p) ? p : new Date(p.toString()))),
  )(boundaries);

  const diff =
    !R.isNil(R.last(formattedAvailablePeriod)) && !R.isNil(lastNValue)
      ? getSubstructedDate(
          R.last(formattedAvailablePeriod),
          lastNValue,
          frequency,
        )
      : null;

  const newStartPeriod =
    !R.isNil(diff) && R.isNil(R.head(period))
      ? getSdmxPeriod(frequency)(diff)
      : R.head(period);

  return {
    startPeriod: R.equals(lastNMode, LASTNPERIODS)
      ? newStartPeriod
      : R.head(period),
    endPeriod: R.last(period),
    lastNObservations,
  };
});

export const getRawDataRequestArgs = memoize((state) => {
  return rawDataRequestArgsWrapper(
    getRawStructureRequestArgs(state),
    getDataquery(state),
    getDataRequestParams(state),
    getExternalReference(state),
    getDataRequestRange(state),
  );
});

export const getDataRequestArgs = memoize((state) => {
  const { datasource, ...rest } = getRawDataRequestArgs(state);
  const args = getRequestArgs({ ...rest, datasource, type: 'data' });
  return R.assoc('datasourceId', R.prop('id', datasource), args);
});

export const getIsDataUrlTooLong = memoize((state) =>
  R.prop('isDataUrlTooLong', getSdmx(state)),
);
export const getIsApiQueryCopied = memoize((state) =>
  R.prop('isApiQueryCopied', getSdmx(state)),
);

export const getDataFileRequestArgs = (isFull) =>
  memoize((state) => {
    const a = getRawDataRequestArgs(state);
    const b = getDisplay(state);
    return dataFileRequestArgsWrapper(isFull)(a, b);
  });

export const getStructureUrl = memoize((state) => {
  const args = getRawStructureRequestArgs(state);
  const externalReference = getExternalReference(state);
  return getSDMXUrl({
    ...R.when(
      R.always(!R.isNil(externalReference)),
      R.mergeLeft(externalReference),
    )(args),
    type: 'dataflow',
  });
});

export const getDataUrl = ({ agnostic }) =>
  memoize((state) =>
    getSDMXUrl({ ...getRawDataRequestArgs(state), agnostic, type: 'data' }),
  );

export const getAvailableConstraintsArgs = (
  dataquery = null,
  filterParams = null,
) =>
  memoize((state) => {
    const args = getRawDataRequestArgs(state);
    return R.pipe(
      R.over(R.lensProp('params'), (params) => {
        if (R.isNil(filterParams) || R.isNil(params)) return params;
        return R.pick(['endPeriod', 'startPeriod'], filterParams || params);
      }),
      R.over(R.lensProp('dataquery'), (dq) => dataquery || dq),
      getRequestArgs,
      R.over(R.lensProp('params'), R.assoc('mode', 'available')),
      R.assoc(
        'supportsPostLongRequests',
        R.propOr(false, 'supportsPostLongRequests', args.datasource),
      ),
      R.assoc('datasourceId', R.path(['datasource', 'id'], args)),
    )({
      ...args,
      withReferences: false,
      type: 'availableconstraint',
    });
  });

export const getIsCsvFileLinkHandled = memoize((state) =>
  R.propOr(true, 'supportsCsvFile', getDatasource(state)),
);
